import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';

/*
 * Services — layar `services` paket client.
 *
 * Kolom tabelnya mengikuti prototipe kecuali satu. Prototipe punya kolom Slug;
 * Category tidak punya kolom itu dan tidak ada yang memakainya, jadi tempatnya
 * dipakai Pricing type — fixed, hourly atau distance — yang justru menentukan
 * bagaimana layanan itu dihitung.
 *
 * Formulirnya LEBIH LENGKAP daripada empat field prototipe, dengan sengaja.
 * Baris Category menyimpan komisi pelanggan dan provider, biaya perjalanan,
 * jenis penetapan harga, dan empat saklar perilaku. Menyembunyikannya berarti
 * layar ini tidak bisa dipakai untuk hal yang paling sering diubah orang
 * — berapa potongan platform — dan memaksa kembali ke dashboard lama.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String, $state: String) {
  getAdminServices(currentPage: $currentPage, limit: $limit, searchList: $searchList, state: $state) {
    count total activeCount status errorMessage
    results {
      id name description status pricingType distanceType currency travellingPrice
      userServiceFeeType userServiceFeeValue partnerServiceFeeType partnerServiceFeeValue
      isPopular isJobPhotoRequired isDirectBooking allowManualAssignment
      subCount bookingCount activePricingCount priceMin priceMax
      lockedOffReason lockedDeleteReason
    }
  }
}`;

// addCategory menangani buat DAN sunting: id kosong berarti membuat.
const M_SAVE = `mutation ($id: Int, $name: String, $description: String,
                 $pricingType: String, $status: String, $currency: String,
                 $distanceType: String, $travellingPrice: Float,
                 $userServiceFeeValue: Float, $partnerServiceFeeValue: Float,
                 $isPopular: Boolean, $isJobPhotoRequired: Boolean, $isDirectBooking: Boolean) {
  addCategory(id: $id, name: $name, description: $description,
              pricingType: $pricingType, status: $status, currency: $currency,
              distanceType: $distanceType, travellingPrice: $travellingPrice,
              userServiceFeeValue: $userServiceFeeValue,
              partnerServiceFeeValue: $partnerServiceFeeValue,
              isPopular: $isPopular, isJobPhotoRequired: $isJobPhotoRequired,
              isDirectBooking: $isDirectBooking) { status errorMessage }
}`;

const M_STATUS = `mutation ($id: Int, $fieldName: String, $fieldValue: String) {
  updateCategoryStatus(id: $id, fieldName: $fieldName, fieldValue: $fieldValue) { status errorMessage }
}`;

const M_DELETE = `mutation ($id: Int) {
  deleteCategory(id: $id) { status errorMessage }
}`;

const PRICING_TYPES = [
  ['fixed', 'Fixed — one price per job', 'ثابت — سعر واحد لكل مهمة'],
  ['hourly', 'Hourly — priced by time on site', 'بالساعة — حسب وقت تنفيذ المهمة'],
  ['distance', 'Distance — priced by kilometres towed', 'بالمسافة — حسب الكيلومترات']
];

/*
 * Ketiga mutation kategori memakai bentuk balasan yang TIDAK sama:
 * addCategory dan updateCategoryStatus mengembalikan status sebagai Int,
 * deleteCategory sebagai String — jadi penghapusan yang berhasil membalas
 * "200", bukan 200. Perbandingan ketat membuatnya terbaca gagal padahal
 * layanannya sudah terhapus, dan orang akan menekannya dua kali.
 */
const ok = res => res && Number(res.status) === 200;

const money = (n, c) => `${c || 'AED'} ${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

const blank = () => ({
  id: null, name: '', description: '', pricingType: 'fixed', status: 'active',
  currency: 'AED', distanceType: 'km', travellingPrice: 0,
  userServiceFeeValue: 0, partnerServiceFeeValue: 0,
  isPopular: false, isJobPhotoRequired: false, isDirectBooking: false
});

class Services extends React.Component {
  state = {
    rows: [], count: 0, total: 0, activeCount: 0,
    page: 1, perPage: 25, search: '', visibility: '',
    loading: true, error: null,
    form: null, formError: null, deleting: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    state: this.state.visibility || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminServices;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load services.', 'تعذر تحميل الخدمات.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0,
      total: res.total || 0, activeCount: res.activeCount || 0, loading: false
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  setVisibility = e => this.setState({ visibility: e.target.value, page: 1 }, () => this.load());

  openAdd = () => this.setState({ form: blank(), formError: null, notice: null });

  openEdit = r => this.setState({
    form: {
      id: r.id, name: r.name, description: r.description || '',
      pricingType: r.pricingType, status: r.status, currency: r.currency || 'AED',
      distanceType: r.distanceType || 'km', travellingPrice: r.travellingPrice,
      userServiceFeeValue: r.userServiceFeeValue,
      partnerServiceFeeValue: r.partnerServiceFeeValue,
      isPopular: r.isPopular, isJobPhotoRequired: r.isJobPhotoRequired,
      isDirectBooking: r.isDirectBooking,
      // Dibawa ikut hanya untuk keterangan di formulir, tidak dikirim balik.
      lockedOffReason: r.lockedOffReason
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submit = async () => {
    const f = this.state.form;
    if (!f.name.trim()) {
      this.setState({ formError: this.tx('A service name is required.', 'اسم الخدمة مطلوب.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    const vars = {
      id: f.id || undefined,
      name: f.name.trim(),
      description: f.description || '',
      pricingType: f.pricingType,
      status: f.status,
      currency: f.currency,
      distanceType: f.pricingType === 'distance' ? f.distanceType : undefined,
      travellingPrice: Number(f.travellingPrice) || 0,
      userServiceFeeValue: Number(f.userServiceFeeValue) || 0,
      partnerServiceFeeValue: Number(f.partnerServiceFeeValue) || 0,
      isPopular: !!f.isPopular,
      isJobPhotoRequired: !!f.isJobPhotoRequired,
      isDirectBooking: !!f.isDirectBooking
    };

    const json = await gql(M_SAVE, vars).catch(() => null);
    const res = json && json.data && json.data.addCategory;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the service.', 'تعذر حفظ الخدمة.') });
      return;
    }
    this.setState({
      form: null,
      notice: { tone: 'info', text: f.id ? `${vars.name} ${this.tx('updated.', 'تم تحديثها.')}` : `${vars.name} ${this.tx('created.', 'تم إنشاؤها.')}` }
    });
    this.load(true);
  };

  toggleStatus = async r => {
    const next = r.status === 'active' ? 'inactive' : 'active';
    this.setState({ saving: true, notice: null });
    const json = await gql(M_STATUS,
      { id: r.id, fieldName: 'status', fieldValue: next }).catch(() => null);
    const res = json && json.data && json.data.updateCategoryStatus;
    this.setState({ saving: false });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not change the status.', 'تعذر تغيير الحالة.') }
        : {
          tone: 'info',
          text: next === 'active'
            ? `${r.name} ${this.tx('is bookable again.', 'متاحة للحجز مرة أخرى.')}`
            : `${r.name} ${this.tx('is hidden from both apps. Existing bookings are unaffected.', 'مخفية من التطبيقين. الحجوزات الحالية لا تتأثر.')}`
        }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.deleteCategory;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the service.', 'تعذر حذف الخدمة.') }
        : { tone: 'danger', text: `${r.name} ${this.tx('was deleted.', 'تم حذفها.')}` }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      {
        key: 'name', label: t('Service', 'الخدمة'), sortable: true,
        render: r => (
          <span>
            <b>{r.name}</b>
            {r.isDirectBooking && (
              <span className="tag amber" style={{ marginInlineStart: 8 }}><i />{t('Direct', 'مباشر')}</span>
            )}
          </span>
        )
      },
      {
        // Prototipe menaruh Slug di sini. Category tidak punya slug, dan jenis
        // penetapan harga jauh lebih menentukan bagi orang yang membaca daftar.
        key: 'pricingType', label: t('Pricing', 'التسعير'), sortable: true,
        render: r => (
          <span className="mono">
            {r.pricingType}{r.pricingType === 'distance' ? ` / ${r.distanceType || 'km'}` : ''}
          </span>
        )
      },
      { key: 'subCount', label: t('Sub-services', 'الخدمات الفرعية'), align: 'end', sortable: true },
      {
        key: 'priceMin', label: t('Price range', 'نطاق السعر'), align: 'end', sortable: true,
        sortValue: r => (r.priceMin === null ? -1 : r.priceMin),
        render: r => r.priceMin === null
          ? <span className="muted">{t('No prices set', 'لم يتم تحديد أسعار')}</span>
          : (
            <span className="num">
              {r.priceMin === r.priceMax
                ? money(r.priceMin, r.currency)
                : `${money(r.priceMin, r.currency)}–${Math.round(r.priceMax).toLocaleString('en-US')}`}
            </span>
          )
      },
      { key: 'bookingCount', label: t('Bookings', 'الحجوزات'), align: 'end', sortable: true },
      {
        key: 'status', label: t('Status', 'الحالة'),
        render: r => (
          <span className={`tag ${r.status === 'active' ? 'ok' : ''}`}>
            <i />{r.status === 'active' ? t('Bookable', 'متاح للحجز') : t('Hidden', 'مخفي')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title="Edit service"
              aria-label={`Edit ${r.name}`} onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button"
              title={r.lockedOffReason || (r.status === 'active' ? 'Hide from the apps' : 'Make bookable')}
              aria-label={`Toggle ${r.name}`}
              disabled={!!r.lockedOffReason}
              onClick={() => this.toggleStatus(r)}>
              <Icon name={r.status === 'active' ? 'x' : 'check'} />
            </button>
            <button className="btn icon" type="button"
              title={r.lockedDeleteReason || 'Delete service'}
              aria-label={`Delete ${r.name}`}
              disabled={!!r.lockedDeleteReason}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError } = this.state;
    const t = this.tx;

    return (
      <Modal
        icon="grid" wide
        title={f.id ? `${t('Edit', 'تعديل')} ${f.name}` : t('Add service', 'إضافة خدمة')}
        sub={f.id
          ? t('Prices themselves live on the Pricing screen, per area and sub-service.', 'توجد الأسعار في شاشة التسعير حسب المنطقة والخدمة الفرعية.')
          : t('A new service is not bookable until it has prices and sub-services.', 'لا يمكن حجز الخدمة الجديدة حتى تُضاف الأسعار والخدمات الفرعية.')}
        confirmLabel={f.id ? t('Save service', 'حفظ الخدمة') : t('Create service', 'إنشاء الخدمة')}
        busy={saving}
        disabled={!f.name.trim()}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

          <Fieldset title={t('Service', 'الخدمة')}>
          <Field label={t('Service name', 'اسم الخدمة')} required>
            <input className="inp" value={f.name} placeholder="Winch Recovery"
              onChange={e => this.setForm('name', e.target.value)} />
          </Field>
          <Field label={t('Pricing type', 'نوع التسعير')}
            hint={t('Decides how the fare is calculated for every booking of this service.', 'يحدد كيفية احتساب أجرة كل حجز لهذه الخدمة.')}>
            <select className="inp" value={f.pricingType}
              onChange={e => this.setForm('pricingType', e.target.value)}>
              {PRICING_TYPES.map(([v, l, ar]) => <option key={v} value={v}>{this.locale() === 'ar' ? ar : l}</option>)}
            </select>
          </Field>
          <Field label={t('Description', 'الوصف')} span>
            <textarea className="inp" rows={2} value={f.description}
              onChange={e => this.setForm('description', e.target.value)} />
          </Field>
          {f.pricingType === 'distance' && (
            <Field label={t('Distance unit', 'وحدة المسافة')}>
              <select className="inp" value={f.distanceType}
                onChange={e => this.setForm('distanceType', e.target.value)}>
                <option value="km">{t('Kilometres', 'كيلومترات')}</option>
                <option value="miles">{t('Miles', 'أميال')}</option>
              </select>
            </Field>
          )}
          <Field label={`${t('Travelling charge', 'رسوم التنقل')} (${f.currency})`}
            hint={t('Added on top of the fare to cover getting to the customer.', 'تُضاف إلى الأجرة لتغطية الوصول إلى العميل.')}>
            <input className="inp" type="number" min="0" step="1" value={f.travellingPrice}
              onChange={e => this.setForm('travellingPrice', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Commission', 'العمولة')}
          desc={t('What the platform keeps from each completed job of this service.', 'ما تحتفظ به المنصة من كل مهمة مكتملة لهذه الخدمة.')}>
          <Field label={t('From the customer (%)', 'من العميل (%)')}
            hint={t('Added to what the customer pays.', 'تُضاف إلى ما يدفعه العميل.')}>
            <input className="inp" type="number" min="0" max="100" step="0.1"
              value={f.userServiceFeeValue}
              onChange={e => this.setForm('userServiceFeeValue', e.target.value)} />
          </Field>
          <Field label={t('From the provider (%)', 'من مقدم الخدمة (%)')}
            hint={t('Deducted from what the provider earns.', 'تُخصم من أرباح مقدم الخدمة.')}>
            <input className="inp" type="number" min="0" max="100" step="0.1"
              value={f.partnerServiceFeeValue}
              onChange={e => this.setForm('partnerServiceFeeValue', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Behaviour', 'السلوك')} rows>
          <SettingRow label={t('Bookable in the apps', 'متاح للحجز في التطبيقات')}
            hint={t('Hidden services keep their history but stop appearing at booking time.', 'تحتفظ الخدمات المخفية بسجلها ولا تظهر وقت الحجز.')}
            note={f.status === 'active' && f.lockedOffReason ? `${t('Locked', 'مقفل')}: ${f.lockedOffReason}` : null}>
            <Switch checked={f.status === 'active'} label={t('Bookable in the apps', 'متاح للحجز في التطبيقات')}
              disabled={f.status === 'active' && !!f.lockedOffReason}
              onChange={v => this.setForm('status', v ? 'active' : 'inactive')} />
          </SettingRow>
          <SettingRow label={t('Direct booking', 'حجز مباشر')}
            hint={t('Customer pays up front and the job waits in the dispatch queue for a provider.', 'يدفع العميل مقدماً وينتظر الطلب في قائمة التوزيع مقدم خدمة.') }>
            <Switch checked={f.isDirectBooking} label={t('Direct booking', 'حجز مباشر')}
              onChange={v => this.setForm('isDirectBooking', v)} />
          </SettingRow>
          <SettingRow label={t('Show as popular', 'عرض كشائعة')}
            hint={t('Given a highlighted spot in the customer app.', 'تظهر في موضع مميز في تطبيق العميل.') }>
            <Switch checked={f.isPopular} label={t('Show as popular', 'عرض كشائعة')}
              onChange={v => this.setForm('isPopular', v)} />
          </SettingRow>
          <SettingRow label={t('Require job photos', 'طلب صور المهمة')}
            hint={t('The provider must photograph the vehicle before and after.', 'يجب على مقدم الخدمة تصوير المركبة قبل وبعد المهمة.') }>
            <Switch checked={f.isJobPhotoRequired} label={t('Require job photos', 'طلب صور المهمة')}
              onChange={v => this.setForm('isJobPhotoRequired', v)} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, activeCount, page, perPage, loading, error, visibility,
      form, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="services"
        breadcrumb={[t('Catalogue', 'الكتالوج'), t('Services', 'الخدمات')]}
        title={t('Services', 'الخدمات')}
        lead={this.locale() === 'ar'
          ? `${total} خدمة رئيسية، ${activeCount} متاحة للحجز في التطبيقات.`
          : `${total} top-level service${total === 1 ? '' : 's'}, ${activeCount} bookable in the apps.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/category" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Add service', 'إضافة خدمة')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {form && this.renderForm()}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={`${t('Delete', 'حذف')} ${deleting.name}؟`}
            sub={t('This removes the service row itself, not just from the apps.', 'سيحذف هذا صف الخدمة نفسه، وليس إخفاءه من التطبيقات فقط.')}
            confirmLabel={t('Delete service', 'حذف الخدمة')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Sub-services', 'الخدمات الفرعية'), String(deleting.subCount)],
              [t('Historical bookings', 'الحجوزات السابقة'), String(deleting.bookingCount)],
              [t('Active price rows', 'صفوف الأسعار النشطة'), String(deleting.activePricingCount)]
            ]} />
            {/* Category.destroy tidak menyentuh Booking.categoryId. Di database
                ini sudah ada booking yang menunjuk kategori 5 yang sudah
                terhapus, dan kolom Service pada riwayatnya kosong selamanya.
                Jadi angkanya disebut, bukan sekadar "tindakan ini permanen". */}
            {deleting.bookingCount > 0 && (
              <div className="notice danger" style={{ marginBottom: 0 }}>
                <span className="ni">!</span>
                <span className="nt">
                  {this.locale() === 'ar'
                    ? `هناك ${deleting.bookingCount} حجوزات سابقة مرتبطة بهذه الخدمة. حذفها لا يحذف الحجوزات، لكنه يترك اسم الخدمة فارغاً في التقارير. لإيقاف الحجوزات الجديدة دون فقدان السجل، أخفِ الخدمة بدلاً من حذفها.`
                    : `${deleting.bookingCount} past booking${deleting.bookingCount === 1 ? '' : 's'} point at this service. Deleting it does not delete them — it leaves them with a blank service name in every report, permanently. To stop new bookings without losing the history, hide it instead.`}
                </span>
              </div>
            )}
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search service name', 'ابحث عن اسم الخدمة')}
              filters={
                <select className="inp" value={visibility} onChange={this.setVisibility}
                  aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                  <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                  <option value="active">{t('Bookable', 'متاح للحجز')}</option>
                  <option value="inactive">{t('Hidden', 'مخفي')}</option>
                </select>
              }
              emptyText={t('No services match', 'لا توجد خدمات مطابقة')}
              emptyHint={t('Try clearing the status filter.', 'جرّب مسح تصفية الحالة.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(Services);
